import { captureException } from "@sentry/nextjs";
import type { JwtPayload } from "@supabase/supabase-js";

interface SubscriptionClaims {
  hasActiveSubscription: boolean;
  hasEverSubscribed: boolean;
  hasLifetime: boolean;
  onboardingCompleted: boolean;
}

export interface AnonymousReader extends SubscriptionClaims {
  email: null;
  id: null;
  isAuthenticated: false;
}

export interface SignedInReader extends SubscriptionClaims {
  email: string | null;
  id: string;
  isAuthenticated: true;
}

export type Reader = AnonymousReader | SignedInReader;

const ANONYMOUS: AnonymousReader = {
  email: null,
  hasActiveSubscription: false,
  hasEverSubscribed: false,
  hasLifetime: false,
  id: null,
  isAuthenticated: false,
  onboardingCompleted: false,
};

export function readerFromClaims(
  claims: JwtPayload | null | undefined
): Reader {
  if (!claims) {
    return ANONYMOUS;
  }
  const app = claims.app_metadata;
  return {
    email: claims.email ?? null,
    hasActiveSubscription: app?.has_active_sub === true,
    hasEverSubscribed: app?.has_ever_sub === true,
    hasLifetime: app?.has_lifetime === true,
    id: claims.sub,
    isAuthenticated: true,
    onboardingCompleted: Boolean(claims.user_metadata?.onboarding_completed),
  };
}

interface ClaimsClient {
  auth: {
    getClaims: () => Promise<{ data: { claims: JwtPayload } | null }>;
  };
}

// A failed claims read is anonymous, so every gate fails closed.
export async function readerFor(
  supabase: ClaimsClient,
  source: "proxy" | "session"
): Promise<Reader> {
  try {
    const { data } = await supabase.auth.getClaims();
    return readerFromClaims(data?.claims);
  } catch (error) {
    console.error(`[${source}] getClaims failed`, error);
    captureException(error, { tags: { [source]: "getClaims" } });
    return ANONYMOUS;
  }
}
